import { useState, useCallback } from 'react';
import { MapPin, Car, Loader2, Calculator, Navigation, Check, X, Clock, Calendar as CalendarIcon, Moon, Sun, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { GooglePlacesAutocomplete, GoogleMapDisplay } from './GoogleMaps';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WAIT_RATE = 0.35;

export const PriceSimulator = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupCoords, setPickupCoords] = useState(null);
  const [destAddress, setDestAddress] = useState('');
  const [destCoords, setDestCoords] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stops, setStops] = useState([]);
  const [pickupHour, setPickupHour] = useState(new Date().getHours());
  const [pickupDate, setPickupDate] = useState(new Date().toISOString().slice(0, 10));
  const [roundTrip, setRoundTrip] = useState(false);

  const totalWaitCost = stops.reduce((sum, s) => sum + (s.waitMinutes || 0) * WAIT_RATE, 0);
  const isNight = pickupHour >= 21 || pickupHour < 6;

  const addStop = () => { if (stops.length < 3) setStops(prev => [...prev, { address: '', coords: null, waitMinutes: 0, customWait: '' }]); };
  const removeStop = (i) => setStops(prev => prev.filter((_, idx) => idx !== i));
  const updateStop = (i, field, val) => setStops(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  const calculatePrice = useCallback(async () => {
    if (!pickupCoords || !destCoords) return;
    setLoading(true);
    try {
      const wpCoords = stops.filter(s => s.coords).map(s => s.coords);
      const response = await axios.post(`${API}/bookings/estimate`, null, {
        params: {
          pickup_lat: pickupCoords.lat, pickup_lng: pickupCoords.lng,
          dest_lat: destCoords.lat, dest_lng: destCoords.lng,
          pickup_hour: pickupHour,
          pickup_address: pickupAddress,
          dest_address: destAddress,
          pickup_date: pickupDate,
          is_round_trip: roundTrip,
          waypoints: wpCoords.length > 0 ? JSON.stringify(wpCoords) : '',
        }
      });
      setEstimate(response.data);
    } catch (error) { console.error('Estimate error:', error); }
    finally { setLoading(false); }
  }, [pickupCoords, destCoords, pickupHour, pickupDate, roundTrip, pickupAddress, destAddress]);

  const handleClose = () => {
    setPickupAddress(''); setPickupCoords(null);
    setDestAddress(''); setDestCoords(null);
    setEstimate(null); setStops([]);
    onClose();
  };

  const bd = estimate?.breakdown || {};
  const finalPrice = estimate ? (estimate.price + totalWaitCost) : null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => { if (e.target.closest('.pac-container')) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <Calculator className="w-5 h-5 text-emerald-500" />
            {t('sim_title')}
          </DialogTitle>
        </DialogHeader>
        
        <p className="text-zinc-400 text-sm">{t('sim_subtitle')}</p>

        <div className="space-y-4 mt-4">
          {/* Pickup */}
          <div className="space-y-2">
            <Label className="text-zinc-300 flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-500" />{t('sim_pickup')}</Label>
            <GooglePlacesAutocomplete placeholder={t('sim_pickup_placeholder')} value={pickupAddress} icon={MapPin}
              onSelect={(r) => { setPickupAddress(r.display_name); setPickupCoords({ lat: r.lat, lng: r.lng }); setEstimate(null); }} />
            {pickupCoords && <div className="flex items-center gap-2 text-xs text-emerald-500"><Check className="w-3 h-3" />{t('sim_address_selected')}</div>}
          </div>

          {/* Destination */}
          <div className="space-y-2">
            <Label className="text-zinc-300 flex items-center gap-2"><Navigation className="w-4 h-4 text-red-500" />{t('sim_destination')}</Label>
            <GooglePlacesAutocomplete placeholder={t('sim_dest_placeholder')} value={destAddress} icon={Navigation}
              onSelect={(r) => { setDestAddress(r.display_name); setDestCoords({ lat: r.lat, lng: r.lng }); setEstimate(null); }} />
            {destCoords && <div className="flex items-center gap-2 text-xs text-emerald-500"><Check className="w-3 h-3" />{t('sim_address_selected')}</div>}
          </div>

          {/* Date & Hour */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300 text-sm flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> Date</Label>
              <Input type="date" value={pickupDate} onChange={(e) => { setPickupDate(e.target.value); setEstimate(null); }}
                className="bg-zinc-800 border-zinc-700 text-white mt-1" />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm flex items-center gap-1">
                {isNight ? <Moon className="w-3 h-3 text-amber-400" /> : <Sun className="w-3 h-3 text-yellow-400" />}
                {t('sim_hour')}
              </Label>
              <Input type="number" min={0} max={23} value={pickupHour}
                onChange={(e) => { setPickupHour(parseInt(e.target.value) || 0); setEstimate(null); }}
                className="bg-zinc-800 border-zinc-700 text-white mt-1" />
              {isNight && <p className="text-amber-400 text-xs mt-1">{t('sim_night_warning')}</p>}
            </div>
          </div>

          {/* Round trip */}
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-zinc-800/50 border border-zinc-700 hover:border-zinc-500 transition">
            <input type="checkbox" checked={roundTrip} onChange={(e) => { setRoundTrip(e.target.checked); setEstimate(null); }}
              className="w-5 h-5 accent-emerald-500" />
            <span className="text-zinc-300 text-sm">{t('round_trip_label')}</span>
          </label>

          {/* Stops */}
          {stops.map((stop, i) => (
            <div key={i} className="p-3 bg-zinc-800/50 rounded-xl border border-zinc-700 relative">
              <button onClick={() => removeStop(i)} className="absolute top-2 right-2 text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
              <Label className="text-zinc-300 text-sm">{t('stop_address')} {i + 1}</Label>
              <div className="mt-1 mb-2">
                <GooglePlacesAutocomplete onSelect={(addr) => { updateStop(i, 'address', addr.display_name || addr); updateStop(i, 'coords', { lat: addr.lat, lng: addr.lng }); }} placeholder={t('stop_placeholder')} />
              </div>
              <Label className="text-zinc-300 text-xs">{t('stop_wait_time')}</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {[0, 5, 10, 15, 20, 30].map(m => (
                  <button key={m} onClick={() => updateStop(i, 'waitMinutes', m)}
                    className={`px-2 py-0.5 rounded text-xs border transition ${stop.waitMinutes === m && !stop.customWait ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}>
                    {m === 0 ? t('stop_no_wait') : `${m} min`}
                  </button>
                ))}
                <Input type="number" min="0" max="120" placeholder={t('stop_custom')} value={stop.customWait}
                  onChange={(e) => { updateStop(i, 'customWait', e.target.value); updateStop(i, 'waitMinutes', parseInt(e.target.value) || 0); }}
                  className="w-20 bg-zinc-800 border-zinc-700 text-white text-xs h-6" />
              </div>
              {stop.waitMinutes > 0 && <p className="text-emerald-400 text-xs mt-1">{t('stop_wait_cost')}: {(stop.waitMinutes * WAIT_RATE).toFixed(2)}€</p>}
            </div>
          ))}
          {stops.length < 3 && (
            <Button variant="outline" onClick={addStop} className="w-full border-dashed border-zinc-600 text-zinc-400 hover:text-emerald-400 hover:border-emerald-500 text-sm h-9">
              + {t('stop_add')}
            </Button>
          )}

          {/* Map */}
          {(pickupCoords || destCoords) && (
            <GoogleMapDisplay pickupCoords={pickupCoords} destCoords={destCoords} height="180px" showRoute={pickupCoords && destCoords} />
          )}

          <Button onClick={calculatePrice} disabled={!pickupCoords || !destCoords || loading} className="w-full btn-primary" data-testid="calculate-price-btn">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t('sim_calculating')}</> : <><Calculator className="w-4 h-4 mr-2" />{t('sim_calculate')}</>}
          </Button>

          {/* Results */}
          {estimate && (
            <div className="p-4 bg-zinc-800 rounded-xl border border-emerald-500/30 animate-fade-in-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <Car className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <p className="text-white font-bold font-['Outfit']">Tesla Model Y</p>
                  <p className="text-zinc-400 text-sm">Premium • 100% {t('sim_electric')}</p>
                </div>
              </div>

              {/* Detailed breakdown */}
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">Distance</span><span className="text-white">{bd.distance_km} km</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">{t('estimate_duration')}</span><span className="text-white">~{Math.round(bd.duration_min || 0)} min</span></div>

                <div className="border-t border-zinc-700 pt-2 mt-2">
                  <div className="flex justify-between"><span className="text-zinc-400">{t('sim_base_price')}</span><span className="text-white">{bd.base_price?.toFixed(2)}€</span></div>
                  {bd.rules_applied?.includes('R2_calculated') && (
                    <p className="text-zinc-500 text-xs ml-2">
                      {bd.price_basis === 'distance' ? t('sim_calc_km') : t('sim_calc_time')} ({bd.km_cost?.toFixed(2)}€ km / {bd.time_cost?.toFixed(2)}€ {t('sim_time_label')})
                    </p>
                  )}
                </div>

                {bd.night_surcharge > 0 && (
                  <div className="flex justify-between">
                    <span className="text-amber-400 flex items-center gap-1"><Moon className="w-3 h-3" />{t('sim_night_surcharge')}</span>
                    <span className="text-amber-400">+{bd.night_surcharge?.toFixed(2)}€</span>
                  </div>
                )}

                {bd.rt_discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-emerald-400">{t('sim_rt_discount')}</span>
                    <span className="text-emerald-400">-{bd.rt_discount?.toFixed(2)}€</span>
                  </div>
                )}

                {totalWaitCost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400">{t('price_stops_wait')}</span>
                    <span className="text-amber-400">+{totalWaitCost.toFixed(2)}€</span>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="pt-3 border-t border-zinc-700">
                <div className="flex justify-between items-center mb-1 text-sm"><span className="text-zinc-400">{t('sim_price_ht')}</span><span className="text-white">{bd.total_ht?.toFixed(2)}€</span></div>
                <div className="flex justify-between items-center mb-2 text-sm"><span className="text-zinc-400">{t('sim_tva')}</span><span className="text-white">{bd.tva_amount?.toFixed(2)}€</span></div>
                <div className="flex justify-between items-center pt-2 border-t border-zinc-700">
                  <span className="text-white font-bold">{t('sim_total_ttc')}</span>
                  <span className="text-2xl font-bold text-emerald-500 font-['Outfit']">{finalPrice?.toFixed(2)}€</span>
                </div>
              </div>

              {/* Disclaimers */}
              <div className="mt-4 text-xs text-zinc-500 space-y-1">
                <p>{t('sim_disclaimer_fixed')}</p>
                <p>{t('sim_disclaimer_night')}</p>
                {bd.airport_transfer === 'saint_tropez' && bd.is_round_trip && <p>{t('sim_disclaimer_st_tropez')}</p>}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
